export type FulfilmentRow = {
  subscriptionId: string;
  correlationId: string | null;
  companyName: string | null;
  crmAccountId: string | null;
  firmId: number;
  companyId: string | null;
  productCode: string;
  productName: string;
  siteStructureId: string | null;
  countryName: string | null;
  region: string | null;
  orderId: string | null;
  startDate: string;
  endDate: string;
  status: string;
  migrationSource: string | null;
  createdAt: string;
};

export type ComparisonResult = {
  subscriptionId: string;
  matchType: string;
  assumed: boolean;
  crmOrderId: string;
  firmNameCrm: string;
  firmNameFulfilment: string;
  countryCrm: string;
  countryFulfilment: string;
  productCrm: string;
  productFulfilment: string;
  productMismatch: boolean;
  crmStartDate: string;
  fulfilmentStartDate: string;
  startDateMismatch: boolean;
  startDiffDays: number;
  crmEndDate: string;
  fulfilmentEndDate: string;
  endDateMismatch: boolean;
  endDiffDays: number;
  bigMismatch: boolean;
  coverageGap: string;
  fulSubCount: number;
};

type CrmRow = Record<string, string>;

const UK_SUBREGIONS = new Set([
  "london",
  "south east",
  "south west",
  "yorkshire",
  "north east",
  "north west",
  "east midlands",
  "west midlands",
  "east anglia",
  "east of england",
  "wales",
  "scotland",
  "northern ireland",
  "uk",
  "united kingdom",
  "england",
]);

const LATAM_REGION_NAMES = new Set([
  "latin america",
  "the legal 500 latin america",
  "latam",
  "latin america: international firms",
]);

const LATAM_COUNTRIES = new Set([
  "brazil",
  "mexico",
  "argentina",
  "chile",
  "colombia",
  "peru",
  "ecuador",
  "uruguay",
  "paraguay",
  "bolivia",
  "venezuela",
  "panama",
  "costa rica",
  "guatemala",
  "honduras",
  "el salvador",
  "nicaragua",
  "dominican republic",
  "puerto rico",
  "cuba",
]);

function normaliseCountry(country: string): string {
  const lower = country.toLowerCase().trim();
  if (UK_SUBREGIONS.has(lower)) return "uk";
  if (LATAM_REGION_NAMES.has(lower)) return "latam";
  return lower;
}

function isLatamCountry(country: string): boolean {
  return LATAM_COUNTRIES.has(country.toLowerCase().trim());
}

const SUFFIX_PATTERN =
  /\b(llp|ltd|llc|inc|plc|lp|pllc|pc|pa|sc|corp|corporation|limited|co|company|group|partners|partnership)\b\.?/gi;

function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const da = new Date(a);
  const db = new Date(b);
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}

function preferCountryMatch(candidates: FulfilmentRow[], crmCountry: string): FulfilmentRow[] {
  const crmLower = crmCountry.toLowerCase().trim();
  const exact = candidates.filter(c => (c.countryName ?? "").toLowerCase().trim() === crmLower);
  if (exact.length > 0) return exact;
  const crmNorm = normaliseCountry(crmCountry);
  const crmIsLatamCountry = isLatamCountry(crmCountry);
  const countryMatched = candidates.filter(c => {
    const fulNorm = normaliseCountry(c.countryName ?? "");
    if (fulNorm !== crmNorm) return false;
    if (crmIsLatamCountry && isLatamCountry(c.countryName ?? "")) return false;
    return true;
  });
  if (countryMatched.length > 0) return countryMatched;
  return candidates;
}

function normaliseName(name: string): string {
  return name
    .replace(SUFFIX_PATTERN, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

const SUBSCRIPTION_TIERS = new Set(["premium", "standard", "basic"]);

export function extractProductKey(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes("premium")) return "premium";
  if (lower.includes("standard")) return "standard";
  if (lower.includes("basic") || lower.includes("core")) return "basic";
  if (lower.includes("amplify") || lower.includes("mondaq")) return "amplify";
  if (lower.includes("spotlight")) return "spotlight";
  if (lower.includes("focus on")) return "focus-on";
  if (
    lower.startsWith("country subscription") ||
    lower.startsWith("global subscription") ||
    lower.startsWith("regional subscription")
  ) {
    return "standard";
  }
  return lower;
}

export function isBasicStandardChange(crmProduct: string, fulfilmentProduct: string): boolean {
  const crmKey = extractProductKey(crmProduct);
  const fulKey = extractProductKey(fulfilmentProduct);
  return (
    (crmKey === "basic" || crmKey === "standard") && (fulKey === "basic" || fulKey === "standard")
  );
}

export type DateFormat = "mdy" | "dmy";

export function detectDateFormat(values: string[]): DateFormat {
  for (const v of values) {
    const match = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!match) continue;
    const a = Number(match[1]);
    const b = Number(match[2]);
    if (a > 12) return "dmy";
    if (b > 12) return "mdy";
  }
  return "mdy";
}

export function convertCrmDate(dateStr: string, format: DateFormat = "mdy"): string {
  if (!dateStr) return "";
  const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    const [, a, b, y] = match;
    const month = format === "dmy" ? b : a;
    const day = format === "dmy" ? a : b;
    return `${y}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return dateStr;
}

const ORDER_ID_HEADERS = ["order id", "order id (order) (order)", "order id (order)"];
const CUSTOMER_HEADERS = ["customer", "account name (customer) (account)"];
const COUNTRY_HEADERS = ["jurisdiction", "country (customer) (account)"];
const PRODUCT_HEADERS = ["product name"];
const FROM_DATE_HEADERS = ["from date"];
const TO_DATE_HEADERS = ["to date"];

function findColumn(headers: string[], candidates: string[]): string | null {
  const normalised = headers.map(h => h.toLowerCase().trim());
  for (const candidate of candidates) {
    const idx = normalised.indexOf(candidate);
    if (idx !== -1) return headers[idx];
  }
  return null;
}

function splitLine(line: string, delimiter: "," | "\t"): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function detectDelimiter(header: string): "," | "\t" {
  return header.split("\t").length - 1 > header.split(",").length - 1 ? "\t" : ",";
}

export function parseCrmCsv(text: string): CrmRow[] {
  const lines = text
    .replace(/^\u{FEFF}/u, "")
    .split(/\r?\n/)
    .filter(l => l.trim() !== "");

  if (lines.length < 2) return [];

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitLine(lines[0], delimiter);

  return lines.slice(1).map(line => {
    const cells = splitLine(line, delimiter);
    const row: CrmRow = {};
    for (let i = 0; i < headers.length; i++) {
      row[headers[i]] = cells[i] ?? "";
    }
    return row;
  });
}

const REQUIRED_FULFILMENT_HEADERS = [
  "subscription_id",
  "company_name",
  "product_code",
  "country_name",
  "start_date",
  "end_date",
];

export function parseFulfilmentCsv(text: string): FulfilmentRow[] {
  const lines = text
    .replace(/^\u{FEFF}/u, "")
    .split(/\r?\n/)
    .filter(l => l.trim() !== "");

  if (lines.length < 2) return [];

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitLine(lines[0], delimiter).map(h => h.toLowerCase().trim());

  const missing = REQUIRED_FULFILMENT_HEADERS.filter(h => !headers.includes(h));
  if (missing.length > 0) {
    throw new Error(
      `Fulfilment CSV is missing required headers: ${missing.join(", ")}. Found: ${headers.join(", ")}`,
    );
  }

  return lines.slice(1).map(line => {
    const cells = splitLine(line, delimiter);
    const cell = (name: string): string => cells[headers.indexOf(name)] ?? "";
    const emptyToNull = (v: string): string | null => (v === "" ? null : v);

    return {
      subscriptionId: cell("subscription_id"),
      correlationId: emptyToNull(cell("correlation_id")),
      companyName: emptyToNull(cell("company_name")),
      crmAccountId: emptyToNull(cell("crm_account_id")),
      firmId: Number.parseInt(cell("firm_id"), 10) || 0,
      companyId: emptyToNull(cell("company_id")),
      productCode: cell("product_code"),
      productName: cell("product_name"),
      siteStructureId: emptyToNull(cell("country_id")),
      countryName: emptyToNull(cell("country_name")),
      region: emptyToNull(cell("region")),
      orderId: emptyToNull(cell("order_id")),
      startDate: cell("start_date"),
      endDate: cell("end_date"),
      status: cell("status"),
      migrationSource: emptyToNull(cell("migration_source")),
      createdAt: cell("created_at"),
    };
  });
}

export function compareCrmWithFulfilment(
  crmRows: CrmRow[],
  fulfilmentRows: FulfilmentRow[],
): ComparisonResult[] {
  const crmHeaders = crmRows.length > 0 ? Object.keys(crmRows[0]) : [];
  const orderIdCol = findColumn(crmHeaders, ORDER_ID_HEADERS);
  const customerCol = findColumn(crmHeaders, CUSTOMER_HEADERS);
  const countryCol = findColumn(crmHeaders, COUNTRY_HEADERS);
  const productCol = findColumn(crmHeaders, PRODUCT_HEADERS);
  const fromDateCol = findColumn(crmHeaders, FROM_DATE_HEADERS);
  const toDateCol = findColumn(crmHeaders, TO_DATE_HEADERS);

  if (!customerCol) {
    throw new Error(
      `Could not find a 'Customer' column in the CRM CSV. Expected one of: ${CUSTOMER_HEADERS.join(", ")}. Found headers: ${crmHeaders.join(", ")}`,
    );
  }

  const byOrderId = new Map<string, FulfilmentRow[]>();
  const byNameProductCountry = new Map<string, FulfilmentRow[]>();
  const byNameCountry = new Map<string, FulfilmentRow[]>();

  for (const row of fulfilmentRows) {
    if (row.orderId) {
      const key = row.orderId.toUpperCase();
      const existing = byOrderId.get(key);
      if (existing) existing.push(row);
      else byOrderId.set(key, [row]);
    }

    const normName = normaliseName(row.companyName ?? "");
    const normCountry = normaliseCountry(row.countryName ?? "");
    const productKey = extractProductKey(row.productCode);

    const npcKey = `${normName}|${productKey}|${normCountry}`;
    const npcExisting = byNameProductCountry.get(npcKey);
    if (npcExisting) npcExisting.push(row);
    else byNameProductCountry.set(npcKey, [row]);

    if (isLatamCountry(row.countryName ?? "")) {
      const latamNpcKey = `${normName}|${productKey}|latam`;
      const latamNpcExisting = byNameProductCountry.get(latamNpcKey);
      if (latamNpcExisting) latamNpcExisting.push(row);
      else byNameProductCountry.set(latamNpcKey, [row]);
    }

    const ncKey = `${normName}|${normCountry}`;
    const ncExisting = byNameCountry.get(ncKey);
    if (ncExisting) ncExisting.push(row);
    else byNameCountry.set(ncKey, [row]);

    if (isLatamCountry(row.countryName ?? "")) {
      const latamNcKey = `${normName}|latam`;
      const latamNcExisting = byNameCountry.get(latamNcKey);
      if (latamNcExisting) latamNcExisting.push(row);
      else byNameCountry.set(latamNcKey, [row]);
    }
  }

  const allDateValues: string[] = [];
  for (const crm of crmRows) {
    if (fromDateCol) allDateValues.push((crm[fromDateCol] ?? "").trim());
    if (toDateCol) allDateValues.push((crm[toDateCol] ?? "").trim());
  }
  const dateFormat = detectDateFormat(allDateValues);

  type ParsedCrmRow = {
    rawOrderId: string;
    crmOrderId: string;
    crmCustomer: string;
    crmCountry: string;
    crmProduct: string;
    crmFromDate: string;
    crmToDate: string;
    crmProductKey: string;
    crmNormName: string;
    crmNormCountry: string;
  };

  const parsed: ParsedCrmRow[] = crmRows.map(crm => {
    const rawOrderId = orderIdCol ? (crm[orderIdCol] ?? "").trim() : "";
    const crmCustomer = customerCol ? (crm[customerCol] ?? "").trim() : "";
    const crmCountry = countryCol ? (crm[countryCol] ?? "").trim() : "";
    const crmProduct = productCol ? (crm[productCol] ?? "").trim() : "";
    return {
      rawOrderId,
      crmOrderId: rawOrderId.toUpperCase(),
      crmCustomer,
      crmCountry,
      crmProduct,
      crmFromDate: fromDateCol ? convertCrmDate((crm[fromDateCol] ?? "").trim(), dateFormat) : "",
      crmToDate: toDateCol ? convertCrmDate((crm[toDateCol] ?? "").trim(), dateFormat) : "",
      crmProductKey: extractProductKey(crmProduct),
      crmNormName: normaliseName(crmCustomer),
      crmNormCountry: normaliseCountry(crmCountry),
    };
  });

  const matched = new Set<string>();
  const ambiguousKeys = new Set<string>();

  const pass1Results = new Map<number, { fulfilment: FulfilmentRow; matchType: string }>();

  for (let i = 0; i < parsed.length; i++) {
    const { crmOrderId, crmProductKey } = parsed[i];
    if (!crmOrderId) continue;
    const candidates = byOrderId.get(crmOrderId);
    if (!candidates) continue;
    const productMatch = candidates.find(
      c => !matched.has(c.subscriptionId) && extractProductKey(c.productCode) === crmProductKey,
    );
    if (productMatch) {
      pass1Results.set(i, { fulfilment: productMatch, matchType: "order_id" });
      matched.add(productMatch.subscriptionId);
    }
  }

  for (let i = 0; i < parsed.length; i++) {
    if (pass1Results.has(i)) continue;
    const { crmOrderId } = parsed[i];
    if (!crmOrderId) continue;
    const candidates = byOrderId.get(crmOrderId);
    if (!candidates) continue;
    const unmatchedCandidates = candidates.filter(c => !matched.has(c.subscriptionId));
    if (unmatchedCandidates.length === 1) {
      pass1Results.set(i, {
        fulfilment: unmatchedCandidates[0],
        matchType: "order_id (product differs)",
      });
      matched.add(unmatchedCandidates[0].subscriptionId);
    }
  }

  const results: ComparisonResult[] = [];

  for (let i = 0; i < parsed.length; i++) {
    const {
      rawOrderId,
      crmCustomer,
      crmCountry,
      crmProduct,
      crmFromDate,
      crmToDate,
      crmProductKey,
      crmNormName,
      crmNormCountry,
    } = parsed[i];

    let fulfilment: FulfilmentRow | undefined;
    let matchType = "NOT FOUND";
    let assumed = false;

    const pass1 = pass1Results.get(i);
    if (pass1) {
      fulfilment = pass1.fulfilment;
      matchType = pass1.matchType;
    }

    if (!fulfilment) {
      const npcKey = `${crmNormName}|${crmProductKey}|${crmNormCountry}`;
      const candidates = byNameProductCountry.get(npcKey);
      if (candidates) {
        const unmatchedCandidates = candidates.filter(c => !matched.has(c.subscriptionId));
        if (unmatchedCandidates.length >= 1) {
          if (unmatchedCandidates.length > 1) ambiguousKeys.add(npcKey);
          const preferred = preferCountryMatch(unmatchedCandidates, crmCountry);
          fulfilment = preferred[0];
          if (ambiguousKeys.has(npcKey)) {
            matchType = "name+product+country (ambiguous)";
            assumed = true;
          } else {
            matchType = "name+product+country";
          }
        }
      }
    }

    if (!fulfilment) {
      const ncKey = `${crmNormName}|${crmNormCountry}`;
      const candidates = byNameCountry.get(ncKey);
      if (candidates) {
        const unmatchedCandidates = preferCountryMatch(
          candidates.filter(c => !matched.has(c.subscriptionId)),
          crmCountry,
        );
        const unmatched = unmatchedCandidates.find(c =>
          SUBSCRIPTION_TIERS.has(extractProductKey(c.productCode)),
        );
        if (unmatched && SUBSCRIPTION_TIERS.has(crmProductKey)) {
          fulfilment = unmatched;
          matchType = "name+country (product differs)";
          assumed = true;
        }
      }
    }

    if (fulfilment && !pass1) matched.add(fulfilment.subscriptionId);

    const fulfilmentProductKey = fulfilment ? extractProductKey(fulfilment.productCode) : "";

    const npcLookup = fulfilment
      ? `${normaliseName(fulfilment.companyName ?? "")}|${extractProductKey(fulfilment.productCode)}|${normaliseCountry(fulfilment.countryName ?? "")}`
      : `${crmNormName}|${crmProductKey}|${crmNormCountry}`;
    const npcGroup = byNameProductCountry.get(npcLookup) ?? [];
    const fulfilmentStartDate = fulfilment?.startDate ?? "";
    const fulfilmentEndDate = fulfilment?.endDate ?? "";
    const fulSubCount = npcGroup.length;

    const startDiffDays = daysBetween(crmFromDate, fulfilmentStartDate);
    const endDiffDays = daysBetween(crmToDate, fulfilmentEndDate);
    const bigMismatch = Math.abs(startDiffDays) > 30 || Math.abs(endDiffDays) > 30;

    const gapDays =
      fulfilment && crmToDate && fulfilmentEndDate ? daysBetween(fulfilmentEndDate, crmToDate) : 0;
    const coverageGap = gapDays > 0 ? `${gapDays} days` : "NO";

    results.push({
      subscriptionId: fulfilment?.subscriptionId ?? "",
      matchType,
      assumed,
      crmOrderId: rawOrderId,
      firmNameCrm: crmCustomer,
      firmNameFulfilment: fulfilment?.companyName ?? "",
      countryCrm: crmCountry,
      countryFulfilment: fulfilment?.countryName ?? "",
      productCrm: crmProduct,
      productFulfilment: fulfilment?.productCode ?? "",
      productMismatch: fulfilment ? crmProductKey !== fulfilmentProductKey : false,
      crmStartDate: crmFromDate,
      fulfilmentStartDate,
      startDateMismatch:
        fulfilment !== undefined && crmFromDate !== "" && crmFromDate !== fulfilmentStartDate,
      startDiffDays,
      crmEndDate: crmToDate,
      fulfilmentEndDate,
      endDateMismatch:
        fulfilment !== undefined && crmToDate !== "" && crmToDate !== fulfilmentEndDate,
      endDiffDays,
      bigMismatch,
      coverageGap,
      fulSubCount,
    });
  }

  return results;
}

export function escapeCsvValue(v: string): string {
  let s = v;
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function crmProductToCode(crmProduct: string, fulfilmentProductCode?: string): string {
  const key = extractProductKey(crmProduct);
  const tier =
    key === "premium"
      ? "PREMIUM"
      : key === "standard"
        ? "STANDARD"
        : key === "basic"
          ? "BASIC"
          : "";
  if (!tier) return "";
  if (fulfilmentProductCode) {
    const lastDash = fulfilmentProductCode.lastIndexOf("-");
    if (lastDash > 0) {
      const prefix = fulfilmentProductCode.substring(0, lastDash);
      return `${prefix}-${tier}`;
    }
  }
  return `SUBSCRIPTION-${tier}`;
}

export function comparisonToCsv(results: ComparisonResult[]): string {
  const headers = [
    "subscription_id",
    "match_type",
    "assumed_match",
    "crm_order_id",
    "firm_name_crm",
    "firm_name_fulfilment",
    "country_crm",
    "country_fulfilment",
    "product_crm",
    "product_fulfilment",
    "product_mismatch",
    "product_code",
    "crm_start_date",
    "fulfilment_start_date",
    "start_date_mismatch",
    "start_diff_days",
    "crm_end_date",
    "fulfilment_end_date",
    "end_date_mismatch",
    "end_diff_days",
    "big_mismatch",
    "coverage_gap",
    "ful_sub_count",
  ];

  const lines = [headers.join(",")];
  for (const r of results) {
    const targetCode = r.productMismatch ? crmProductToCode(r.productCrm, r.productFulfilment) : "";
    lines.push(
      [
        r.subscriptionId,
        r.matchType,
        String(r.assumed),
        r.crmOrderId,
        r.firmNameCrm,
        r.firmNameFulfilment,
        r.countryCrm,
        r.countryFulfilment,
        r.productCrm,
        r.productFulfilment,
        String(r.productMismatch),
        targetCode,
        r.crmStartDate,
        r.fulfilmentStartDate,
        String(r.startDateMismatch),
        String(r.startDiffDays),
        r.crmEndDate,
        r.fulfilmentEndDate,
        String(r.endDateMismatch),
        String(r.endDiffDays),
        String(r.bigMismatch),
        r.coverageGap,
        String(r.fulSubCount),
      ]
        .map(escapeCsvValue)
        .join(","),
    );
  }

  return lines.join("\n");
}

export function buildMismatchCsv(results: ComparisonResult[]): string {
  const headers = ["subscription_id", "start_date", "end_date", "product_code"];
  const lines = [headers.join(",")];
  for (const r of results) {
    if (!r.subscriptionId || r.assumed) continue;
    const hasDateMismatch = r.startDateMismatch || r.endDateMismatch;
    const hasAllowedProductChange =
      r.productMismatch && isBasicStandardChange(r.productCrm, r.productFulfilment);
    if (!hasDateMismatch && !hasAllowedProductChange) continue;
    const targetCode = hasAllowedProductChange
      ? crmProductToCode(r.productCrm, r.productFulfilment)
      : "";
    lines.push(
      [r.subscriptionId, r.crmStartDate, r.crmEndDate, targetCode].map(escapeCsvValue).join(","),
    );
  }
  return lines.join("\n");
}
